import type { ModuleId, TrialResult } from '../../core/types';

export interface ReversalThresholdOptions {
  moduleId: ModuleId;
  metadataKey: string;
  lastReversals?: number;
}

function finiteMetadataValue(result: TrialResult, key: string): number | undefined {
  const value = result.metadata[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * Returns the presented values at direction changes in a staircase. Repeated
 * presentations at one level do not count as movement. A new session resets
 * direction so its starting level cannot create a synthetic reversal.
 */
export function collectReversalPoints(
  results: readonly TrialResult[],
  options: ReversalThresholdOptions,
): number[] {
  const reversals: number[] = [];
  let previousSessionId: string | undefined;
  let previousValue: number | undefined;
  let previousDirection: -1 | 0 | 1 = 0;

  for (const result of results) {
    if (!result.valid || result.moduleId !== options.moduleId) continue;
    const value = finiteMetadataValue(result, options.metadataKey);
    if (value === undefined) continue;

    if (result.sessionId !== previousSessionId) {
      previousSessionId = result.sessionId;
      previousValue = value;
      previousDirection = 0;
      continue;
    }
    if (previousValue === undefined) {
      previousValue = value;
      continue;
    }

    const delta = value - previousValue;
    if (Math.abs(delta) <= Number.EPSILON) continue;
    const direction: -1 | 1 = delta > 0 ? 1 : -1;
    if (previousDirection !== 0 && direction !== previousDirection) {
      // `previousValue` is the level at which the staircase turned.
      reversals.push(previousValue);
    }
    previousDirection = direction;
    previousValue = value;
  }

  return reversals;
}

export function estimateReversalThreshold(
  results: readonly TrialResult[],
  options: ReversalThresholdOptions,
): number | undefined {
  const count = Math.max(1, Math.trunc(options.lastReversals ?? 6));
  const sample = collectReversalPoints(results, options).slice(-count);
  if (sample.length === 0) return undefined;
  return sample.reduce((sum, value) => sum + value, 0) / sample.length;
}
